import axios from "axios";
import { ref, type Ref } from "vue";
import api from "../services/api.ts";
import { portalApiPrefix } from "../utils/portal";

/**
 * IP geolocation data returned by the batch lookup endpoint.
 * Fields mirror the backend `ipinfo.IPInfo` struct (JSON tags).
 */
export interface IPInfoData {
  ip: string;
  country?: string;
  country_code?: string;
  continent?: string;
  continent_code?: string;
  asn?: string;
  as_name?: string;
  as_domain?: string;
  is_private?: boolean;
}

/**
 * Reactive state returned per IP address.
 */
export interface UseIPInfoReturn {
  /** True while the batch request containing this IP is in flight */
  loading: Ref<boolean>;
  /** Resolved geolocation data, or null if not yet loaded / not found */
  data: Ref<IPInfoData | null>;
  /** Error message if the batch request failed, otherwise null */
  error: Ref<string | null>;
}

/** Debounce window (ms) for collecting IPs before firing a batch request */
const BATCH_DEBOUNCE_MS = 50;

/** Maximum IPs per batch request (backend enforces 100) */
const MAX_BATCH_SIZE = 100;

// Both routes return the same payload behind different auth middleware, so the
// prefix follows the portal.
function ipInfoEndpoint(): string {
  return `${portalApiPrefix()}/ipinfo`;
}

// ---------------------------------------------------------------------------
// Module-level singleton state
// ---------------------------------------------------------------------------

/**
 * Whether the IPInfo database is available on the backend.
 * - `null`  = unknown (no request has completed yet)
 * - `true`  = DB is loaded, flags should be shown
 * - `false` = DB is not loaded (503), hide flags entirely
 */
export const ipInfoAvailable = ref<boolean | null>(null);

/**
 * Resolved IP data that never needs re-fetching. A `null` entry records an
 * address the backend had no data for, so a remount does not ask again.
 */
const cache = new Map<string, IPInfoData | null>();

/**
 * Reactive refs shared across all components that requested the same IP.
 * Entries are removed once the batch resolves (data moves to `cache`).
 */
const pending = new Map<string, UseIPInfoReturn>();

/** IPs waiting to be included in the next batch request */
const pendingIps = new Set<string>();

/** Handle for the scheduled flush timeout (null when no flush is pending) */
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * No request goes out before this epoch-millis time, set when the backend
 * returns 429. Without it a rate-limited SPA fires a lookup for every address
 * it renders and holds itself over the limit.
 */
let retryAfter = 0;

/** Fallback cooldown (ms) when a 429 carries no usable Retry-After header */
const DEFAULT_RETRY_AFTER_MS = 60_000;

/** Reads the Retry-After header (RFC 6585 delta-seconds) off a 429. */
function cooldownFromResponse(response: { headers?: Record<string, unknown> } | undefined): number {
  const header = response?.headers?.["retry-after"];
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return DEFAULT_RETRY_AFTER_MS;
  }
  return seconds * 1000;
}

// ---------------------------------------------------------------------------
// Batch flush logic
// ---------------------------------------------------------------------------

/**
 * Collects all pending IPs, deduplicates against the cache, and fires a
 * single POST to the portal's ipinfo route. On completion every pending ref
 * is updated and resolved IPs are moved into the cache.
 */
async function flushBatch(): Promise<void> {
  flushTimer = null;

  // Snapshot and clear the pending set so new calls during the request
  // start a fresh batch.
  const ips = Array.from(pendingIps);
  pendingIps.clear();

  // Remove IPs that were cached between scheduling and flushing
  const uncached = ips.filter((ip) => !cache.has(ip));
  if (uncached.length === 0) {
    return;
  }

  // Respect backend batch-size limit by chunking
  const chunks = chunkArray(uncached, MAX_BATCH_SIZE);

  async function processChunk(chunk: string[]): Promise<void> {
    try {
      const response = await api.post<Record<string, IPInfoData>>(ipInfoEndpoint(), {
        ips: chunk,
      });

      // The backend returns 200 with this header set to "false" when no GeoIP
      // database is configured (a common self-hosted setup) so it can degrade
      // silently. Treat that as "unavailable" and hide flags.
      ipInfoAvailable.value = response.headers?.["x-ipinfo-available"] !== "false";
      const results = response.data;

      for (const ip of chunk) {
        const entry = pending.get(ip);
        if (!entry) continue;

        // Cache the miss too. The backend already said it has nothing for this
        // address, so the next mount would just ask for the same answer.
        const info = results[ip] ?? null;
        cache.set(ip, info);
        entry.data.value = info;
        entry.loading.value = false;
        pending.delete(ip);
      }
    } catch (err: unknown) {
      // Backend returns 503 when the IPInfo database is not loaded
      if (axios.isAxiosError(err) && err.response?.status === 503) {
        ipInfoAvailable.value = false;
      }

      // Back off, or the next view re-asks for every address it renders.
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        retryAfter = Date.now() + cooldownFromResponse(err.response);
      }

      const message = err instanceof Error ? err.message : "IP info lookup failed";

      // Mark every IP in this chunk as errored (do NOT cache errors)
      for (const ip of chunk) {
        const entry = pending.get(ip);
        if (!entry) continue;

        entry.error.value = message;
        entry.loading.value = false;
        pending.delete(ip);
      }
    }
  }

  await Promise.allSettled(chunks.map((chunk) => processChunk(chunk)));
}

/**
 * Schedules a batch flush if one is not already pending.
 */
function scheduleFlush(): void {
  if (flushTimer === null) {
    flushTimer = setTimeout(flushBatch, BATCH_DEBOUNCE_MS);
  }
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/** Splits an array into chunks of at most `size` elements */
function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Public composable
// ---------------------------------------------------------------------------

/**
 * Returns reactive geolocation data for a single IP address.
 *
 * Requests are automatically batched: multiple calls within a 50 ms window
 * are collected into a single POST. Resolved data is cached in memory so each
 * IP is fetched at most once per page load.
 *
 * Works in both portals: the user SPA calls `/api/ipinfo` (verified user
 * session), the admin SPA `/api/admin/ipinfo` (verified admin session).
 *
 * @example
 * ```ts
 * const { loading, data, error } = useIPInfo("1.2.3.4");
 * // In template: <span v-if="!loading">{{ data?.country }}</span>
 * ```
 */
export function useIPInfo(ip: string): UseIPInfoReturn {
  // Edge case: empty / falsy IP
  if (!ip) {
    return { loading: ref(false), data: ref(null), error: ref(null) };
  }

  // If we already know the DB is unavailable, don't make any requests
  if (ipInfoAvailable.value === false) {
    return { loading: ref(false), data: ref(null), error: ref(null) };
  }

  // Already resolved: return cached data immediately. A cached miss counts,
  // hence `has` and not a truthiness check.
  if (cache.has(ip)) {
    return { loading: ref(false), data: ref(cache.get(ip) ?? null), error: ref(null) };
  }

  // Already pending: return the same reactive refs (dedup)
  const existing = pending.get(ip);
  if (existing) {
    return existing;
  }

  // Rate limited: wait the window out rather than spend the next one re-firing.
  if (Date.now() < retryAfter) {
    return { loading: ref(false), data: ref(null), error: ref(null) };
  }

  // New IP: create reactive refs, register for batching
  const entry: UseIPInfoReturn = {
    loading: ref(true),
    data: ref(null),
    error: ref(null),
  };

  pending.set(ip, entry);
  pendingIps.add(ip);
  scheduleFlush();

  return entry;
}

/** @internal: only for use in tests */
export function _resetForTesting(): void {
  cache.clear();
  pending.clear();
  pendingIps.clear();
  ipInfoAvailable.value = null;
  retryAfter = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
}
